/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react'
import { IPaginatedList, IPagination } from '../apilibrary/types/common'

export type FetchFunction <T> = (pagination: IPagination) => Promise<IPaginatedList<T>>

interface UsePaginationListParams<T> {
  fetchFunction: FetchFunction<T>
  nextToken?: string
  pageSize?: number // default 10
}

interface UsePaginationListReturn<T> {
  items: T[]
  setItems: (items: T[]) => void
  loading: boolean
  hasMore: boolean
  errorMessage?: string
  reset: (params: UsePaginationListParams<T>) => Promise<void>
  fetchNextPage: () => Promise<void>
}

export function usePaginatedList <T> (
  params: UsePaginationListParams<T>
): UsePaginationListReturn<T> {
  const {
    fetchFunction: inputFetchFunction,
    nextToken: initialNextToken,
    pageSize: inputPageSize = 10
  } = params
  const [nextToken, setNextFetchToken] = useState(initialNextToken)
  const [fetchFunction, setFetchFuntion] = useState<FetchFunction<T>>(() => inputFetchFunction)
  const [pageSize, setPageSize] = useState<number>(inputPageSize)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [items, setItems] = useState<T[]>([])

  const fetchNextPage = async (): Promise<void> => {
    try {
      setErrorMessage(undefined)
      setLoading(true)
      const {
        items: newItems,
        nextToken: newNextToken
      } = await fetchFunction({
        limit: pageSize,
        nextToken
      })
      setItems([...items, ...newItems])
      setNextFetchToken(newNextToken)
      setHasMore(!!newNextToken)
    } catch (e) {
      if (e instanceof Error) {
        setErrorMessage(e.message)
      }
      throw e
    } finally {
      setLoading(false)
    }
  }

  const reset = async (params: UsePaginationListParams<T>): Promise<void> => {
    const {
      fetchFunction,
      nextToken: initialNextToken,
      pageSize = 10
    } = params
    setItems([])
    setNextFetchToken(initialNextToken)
    setPageSize(pageSize)
    setFetchFuntion(() => fetchFunction)
    setHasMore(true)
    setLoading(false)
    setErrorMessage(undefined)
  }

  useEffect(() => {
    void fetchNextPage()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFunction])

  useEffect(() => {
    void fetchNextPage()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    items,
    setItems,
    reset,
    errorMessage,
    hasMore,
    loading,
    fetchNextPage
  }
}
